import React, { useState, useRef } from 'react'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import theme from '../config/theme'
import { FiCalendar } from 'react-icons/fi'
import { IoChevronBack, IoChevronForward } from 'react-icons/io5'

interface DateInputProps {
  label: string
  value: string
  onChange: (value: string) => void
  placeholder?: string
  required?: boolean
  className?: string
  minDate?: Date | null
  maxDate?: Date | null
}

const DateInput: React.FC<DateInputProps> = ({
  label,
  value,
  onChange,
  placeholder = 'Select date',
  required = false,
  className = '',
  minDate,
  maxDate
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [tempDate, setTempDate] = useState<Date | null>(null)
  const datePickerRef = useRef<DatePicker>(null)

  // Convert string value to Date object
  const selectedDate = value ? new Date(value) : null

  // Handle date selection (temporary - for preview)
  const handleDateSelect = (date: Date | null) => {
    setTempDate(date)
  }

  // Handle confirm selection
  const handleConfirm = () => {
    if (tempDate) {
      const year = tempDate.getFullYear()
      const month = String(tempDate.getMonth() + 1).padStart(2, '0')
      const day = String(tempDate.getDate()).padStart(2, '0')
      onChange(`${year}-${month}-${day}`)
    }
    setIsOpen(false)
    setTempDate(null)
  }

  // Handle cancel
  const handleCancel = () => {
    setIsOpen(false)
    setTempDate(null)
  }

  // Format date from YYYY-MM-DD to DD/MM/YYYY
  const formatDateDisplay = (dateString: string): string => {
    if (!dateString) return ''
    const [year, month, day] = dateString.split('-')
    return `${day}/${month}/${year}`
  }

  // Custom header component
  const renderCustomHeader = ({
    date,
    decreaseMonth,
    increaseMonth,
    prevMonthButtonDisabled,
    nextMonthButtonDisabled,
  }: any) => {
    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ]
    const monthName = monthNames[date.getMonth()]

    return (
      <div
        className="flex items-center justify-between px-4 py-3 border-b"
        style={{
          borderColor: theme.color.light300,
          backgroundColor: theme.color.light100
        }}
      >
        <button
          type="button"
          onClick={decreaseMonth}
          disabled={prevMonthButtonDisabled}
          className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed border flex items-center justify-center"
          style={{ color: theme.color.gray500, borderColor: theme.color.light300 }}
        >
          {React.createElement(IoChevronBack as any, { size: 16 })}
        </button>
        <div className="font-semibold" style={{ color: theme.color.gray500 }}>
          {monthName}
        </div>
        <button
          type="button"
          onClick={increaseMonth}
          disabled={nextMonthButtonDisabled}
          className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed border flex items-center justify-center"
          style={{ color: theme.color.gray500, borderColor: theme.color.light300 }}
        >
          {React.createElement(IoChevronForward as any, { size: 16 })}
        </button>
      </div>
    )
  }

  // Custom input component
  const CustomInput = React.forwardRef<HTMLDivElement, any>(({ value, onClick }, ref) => (
    <div
      ref={ref}
      className="w-full px-4 py-3 pr-12 rounded-lg border font-light cursor-pointer"
      style={{
        borderColor: isOpen ? theme.brandColor.red : theme.color.light500,
        backgroundColor: theme.color.light100,
        color: value ? theme.color.gray500 : theme.color.gray100,
      }}
      onClick={onClick}
    >
      {value || placeholder}
    </div>
  ))

  CustomInput.displayName = 'CustomInput'

  return (
    <div className={`relative ${className}`}>
      <label className="block text-sm mb-2" style={{ color: theme.color.gray400 }}>
        {label}
        {required && <span className="ml-1" style={{ color: theme.brandColor.red }}>*</span>}
      </label>
      <div className="relative">
        <DatePicker
          ref={datePickerRef}
          selected={tempDate || selectedDate}
          onSelect={handleDateSelect}
          onChange={handleDateSelect}
          open={isOpen}
          onInputClick={() => setIsOpen(true)}
          onClickOutside={handleCancel}
          dateFormat="dd/MM/yyyy"
          placeholderText={placeholder}
          required={required}
          minDate={minDate || undefined}
          maxDate={maxDate || undefined}
          customInput={<CustomInput />}
          renderCustomHeader={renderCustomHeader}
          calendarClassName="custom-calendar"
          popperClassName="custom-popper"
          formatWeekDay={(nameOfDay) => nameOfDay.substring(0, 2)}
          dayClassName={(date: Date) => {
            const currentDate = tempDate || selectedDate
            if (!currentDate) return ''
            const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
            const currentStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(currentDate.getDate()).padStart(2, '0')}`
            return dateStr === currentStr ? 'selected-day' : ''
          }}
          calendarContainer={({ children, className }) => (
            <div className={`${className} custom-calendar-wrapper`}>
              {children}
              <div className="custom-calendar-footer flex justify-end gap-3 px-4 py-3 border-t bg-white rounded-b-lg" style={{ borderColor: theme.color.light300 }}>
                <button
                  type="button"
                  onClick={handleCancel}
                  className="px-4 py-2 rounded-lg border text-sm font-medium"
                  style={{
                    borderColor: theme.color.light500,
                    backgroundColor: theme.color.light100,
                    color: theme.color.gray500
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirm}
                  className="px-4 py-2 rounded-lg border-0 text-sm font-medium text-white"
                  style={{
                    backgroundColor: theme.brandColor.red,
                    color: theme.color.light100
                  }}
                >
                  Select
                </button>
              </div>
            </div>
          )}
        />
        {/* Calendar icon */}
        <div className="absolute right-4 top-1/2 transform -translate-y-1/2 pointer-events-none z-20">
          {React.createElement(FiCalendar as any, {
            className: "w-5 h-5",
            style: { color: theme.color.gray300 }
          })}
        </div>
      </div>

      <style>{`
        .custom-calendar-wrapper {
          display: flex !important;
          flex-direction: column !important;
          background: white !important;
          border-radius: 0.5rem !important;
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05) !important;
          overflow: hidden !important;
        }
        .react-datepicker-popper {
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05) !important;
        }
        .react-datepicker {
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05) !important;
        }
        .custom-calendar {
          border: none !important;
          border-radius: 0 !important;
          box-shadow: none !important;
          overflow: visible !important;
          background: white !important;
          padding: 0 !important;
        }
        .custom-calendar-footer {
          flex-shrink: 0 !important;
        }
        .custom-calendar .react-datepicker__header {
          background-color: white !important;
          border-bottom: none !important;
          padding: 0 !important;
        }
        .custom-calendar .react-datepicker__day-names {
          display: flex !important;
          justify-content: space-between !important;
          padding: 0.5rem 1rem !important;
          border-bottom: 1px solid ${theme.color.light300} !important;
          margin: 0 !important;
        }
        .custom-calendar .react-datepicker__day-name {
          color: ${theme.color.gray300} !important;
          font-size: 0.875rem !important;
          font-weight: 500 !important;
          width: 2rem !important;
          margin: 0 !important;
          text-transform: capitalize !important;
        }
        .custom-calendar .react-datepicker__month {
          padding: 0.5rem 1rem !important;
          margin: 0 !important;
        }
        .custom-calendar .react-datepicker__week {
          display: flex !important;
          justify-content: space-between !important;
        }
        .custom-calendar .react-datepicker__day {
          width: 2rem !important;
          height: 2rem !important;
          line-height: 2rem !important;
          margin: 0.25rem 0 !important;
          border-radius: 50% !important;
          color: ${theme.color.gray500} !important;
          font-size: 0.875rem !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
        }
        .custom-calendar .react-datepicker__day:hover {
          background-color: ${theme.color.light200} !important;
          border-radius: 50% !important;
        }
        .custom-calendar .react-datepicker__day--selected,
        .custom-calendar .selected-day {
          background-color: ${theme.brandColor.red} !important;
          color: white !important;
          border-radius: 50% !important;
          font-weight: 600 !important;
        }
        .custom-calendar .react-datepicker__day--outside-month {
          color: ${theme.color.gray200} !important;
        }
        .custom-calendar .react-datepicker__month-container {
          padding-bottom: 0 !important;
        }
        .custom-popper {
          z-index: 50 !important;
          width: 100% !important;
        }
        .react-datepicker-popper {
          z-index: 50 !important;
          width: 100% !important;
        }
        .react-datepicker {
          width: 100% !important;
        }
        .custom-calendar-wrapper {
          width: 100% !important;
        }
        .react-datepicker__triangle {
          display: none !important;
        }
      `}</style>
    </div>
  )
}

export default DateInput

